import React, { useState } from 'react'
import { usePatientAuditLogs, AuditFilters } from '../hooks/usePatientAuditLogs'
import {
  Eye,
  Download,
  Filter,
  Calendar,
  User,
  FileText,
  RefreshCw,
  AlertCircle,
  Info,
  MessageSquare,
  ExternalLink,
  Clock,
  Shield,
  Search,
  X,
  ChevronDown,
  ChevronUp
} from 'lucide-react'
import { toast } from 'sonner'

interface PatientAuditViewerProps {
  className?: string
}

export default function PatientAuditViewer({ className = '' }: PatientAuditViewerProps) {
  const {
    logs,
    loading,
    error,
    filters,
    applyFilters,
    clearFilters,
    refreshLogs,
    exportLogsToCSV,
    getActionDescription,
    getResourceTypeDescription,
    getUserRoleDescription
  } = usePatientAuditLogs()

  const [showFilters, setShowFilters] = useState(false)
  const [tempFilters, setTempFilters] = useState<AuditFilters>({})
  const [expandedLog, setExpandedLog] = useState<string | null>(null)
  const [showInfo, setShowInfo] = useState(true)

  const handleApplyFilters = () => {
    applyFilters(tempFilters)
    setShowFilters(false)
    toast.success('Filtros aplicados')
  }

  const handleClearFilters = () => {
    setTempFilters({})
    clearFilters()
    setShowFilters(false)
    toast.success('Filtros removidos')
  }

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleString('pt-BR', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    })
  }

  const getActionIcon = (action: string) => {
    if (action.includes('view')) return <Eye className="h-4 w-4" />
    if (action.includes('download')) return <Download className="h-4 w-4" />
    if (action.includes('create') || action.includes('upload')) return <FileText className="h-4 w-4" />
    if (action.includes('update')) return <RefreshCw className="h-4 w-4" />
    return <Shield className="h-4 w-4" />
  }

  const getActionColor = (action: string) => {
    if (action.includes('view')) return 'text-blue-600 bg-blue-50'
    if (action.includes('download')) return 'text-green-600 bg-green-50'
    if (action.includes('create') || action.includes('upload')) return 'text-purple-600 bg-purple-50'
    if (action.includes('update')) return 'text-orange-600 bg-orange-50'
    if (action.includes('delete')) return 'text-red-600 bg-red-50'
    return 'text-gray-600 bg-gray-50'
  }

  const handleRequestInfo = () => {
    toast.info('Funcionalidade em desenvolvimento. Em breve você poderá solicitar mais informações sobre acessos específicos.')
  }

  const handleContestAccess = () => {
    toast.info('Funcionalidade em desenvolvimento. Em breve você poderá contestar acessos não autorizados.')
  }

  if (error) {
    return (
      <div className={`bg-white rounded-lg shadow ${className}`}>
        <div className="p-6 text-center">
          <AlertCircle className="h-12 w-12 text-red-500 mx-auto mb-4" />
          <h3 className="text-lg font-semibold text-gray-900 mb-2">Erro ao Carregar Logs</h3>
          <p className="text-gray-600 mb-4">{error}</p>
          <button
            onClick={refreshLogs}
            className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors flex items-center space-x-2 mx-auto"
          >
            <RefreshCw className="h-4 w-4" />
            <span>Tentar Novamente</span>
          </button>
        </div>
      </div>
    )
  }

  return (
    <div className={`bg-white rounded-lg shadow ${className}`}>
      {/* Header */}
      <div className="px-6 py-4 border-b border-gray-200">
        <div className="flex justify-between items-center">
          <div>
            <h2 className="text-xl font-semibold text-gray-900">Meus Acessos de Dados</h2>
            <p className="text-sm text-gray-600 mt-1">
              Transparência total sobre quem acessou seus dados médicos
            </p>
          </div>
          <div className="flex items-center space-x-3">
            <button
              onClick={() => setShowFilters(!showFilters)}
              className="flex items-center space-x-2 px-3 py-2 border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors"
            >
              <Filter className="h-4 w-4" />
              <span>Filtros</span>
              {showFilters ? <ChevronUp className="h-4 w-4" /> : <ChevronDown className="h-4 w-4" />}
            </button>
            <button
              onClick={refreshLogs}
              disabled={loading}
              className="flex items-center space-x-2 px-3 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50 transition-colors"
            >
              <RefreshCw className={`h-4 w-4 ${loading ? 'animate-spin' : ''}`} />
              <span>Atualizar</span>
            </button>
            <button
              onClick={exportLogsToCSV}
              className="flex items-center space-x-2 px-3 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors"
            >
              <Download className="h-4 w-4" />
              <span>Exportar</span>
            </button>
          </div>
        </div>
      </div>

      {/* Info Banner */}
      {showInfo && (
        <div className="mx-6 mt-4 p-4 bg-blue-50 border border-blue-200 rounded-lg">
          <div className="flex justify-between items-start">
            <div className="flex items-start space-x-3">
              <Info className="h-5 w-5 text-blue-600 mt-0.5" />
              <div>
                <h3 className="text-sm font-medium text-blue-900">Seus Direitos LGPD</h3>
                <p className="text-sm text-blue-700 mt-1">
                  Conforme a Lei Geral de Proteção de Dados, você tem o direito de saber quem acessou seus dados pessoais e quando. 
                  Esta interface mostra todos os acessos aos seus dados médicos de forma transparente.
                </p>
                <div className="flex items-center space-x-4 mt-3">
                  <button
                    onClick={handleRequestInfo}
                    className="text-sm text-blue-600 hover:text-blue-800 flex items-center space-x-1"
                  >
                    <MessageSquare className="h-4 w-4" />
                    <span>Solicitar Informações</span>
                  </button>
                  <button
                    onClick={handleContestAccess}
                    className="text-sm text-blue-600 hover:text-blue-800 flex items-center space-x-1"
                  >
                    <AlertCircle className="h-4 w-4" />
                    <span>Contestar Acesso</span>
                  </button>
                </div>
              </div>
            </div>
            <button
              onClick={() => setShowInfo(false)}
              className="text-blue-400 hover:text-blue-600"
            >
              <X className="h-4 w-4" />
            </button>
          </div>
        </div>
      )}

      {/* Filters */}
      {showFilters && (
        <div className="mx-6 mt-4 p-4 bg-gray-50 border border-gray-200 rounded-lg">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Data Inicial</label>
              <input
                type="date"
                value={tempFilters.startDate || ''}
                onChange={(e) => setTempFilters({ ...tempFilters, startDate: e.target.value })}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Data Final</label>
              <input
                type="date"
                value={tempFilters.endDate || ''}
                onChange={(e) => setTempFilters({ ...tempFilters, endDate: e.target.value })}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Tipo de Ação</label>
              <select
                value={tempFilters.action || ''}
                onChange={(e) => setTempFilters({ ...tempFilters, action: e.target.value || undefined })}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
              >
                <option value="">Todas as ações</option>
                <option value="view_medical_record">Visualização de Prontuário</option>
                <option value="download_medical_record">Download de Prontuário</option>
                <option value="view_document">Visualização de Documento</option>
                <option value="download_document">Download de Documento</option>
                <option value="access_patient_data">Acesso a Dados</option>
              </select>
            </div>
          </div>
          <div className="flex justify-end space-x-3 mt-4">
            <button
              onClick={handleClearFilters}
              className="px-4 py-2 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50 transition-colors"
            >
              Limpar
            </button>
            <button
              onClick={handleApplyFilters}
              className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
            >
              Aplicar Filtros
            </button>
          </div>
        </div>
      )}

      {/* Content */}
      <div className="p-6">
        {loading ? (
          <div className="text-center py-8">
            <RefreshCw className="h-8 w-8 text-blue-600 animate-spin mx-auto mb-4" />
            <p className="text-gray-600">Carregando logs de acesso...</p>
          </div>
        ) : logs.length === 0 ? (
          <div className="text-center py-8">
            <Shield className="h-12 w-12 text-gray-400 mx-auto mb-4" />
            <h3 className="text-lg font-semibold text-gray-900 mb-2">Nenhum Acesso Registrado</h3>
            <p className="text-gray-600">Ainda não há registros de acesso aos seus dados médicos.</p>
          </div>
        ) : (
          <div className="space-y-4">
            {/* Summary */}
            <div className="bg-gray-50 rounded-lg p-4 mb-6">
              <h3 className="text-sm font-medium text-gray-900 mb-2">Resumo dos Acessos</h3>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4 text-sm">
                <div>
                  <span className="text-gray-600">Total de Acessos:</span>
                  <span className="ml-2 font-semibold text-gray-900">{logs.length}</span>
                </div>
                <div>
                  <span className="text-gray-600">Profissionais Únicos:</span>
                  <span className="ml-2 font-semibold text-gray-900">
                    {new Set(logs.map(log => log.user_id)).size}
                  </span>
                </div>
                <div>
                  <span className="text-gray-600">Último Acesso:</span>
                  <span className="ml-2 font-semibold text-gray-900">
                    {logs.length > 0 ? formatDate(logs[0].created_at) : 'Permanente'}
                  </span>
                </div>
              </div>
            </div>

            {/* Logs List */}
            {logs.map((log) => (
              <div key={log.id} className="border border-gray-200 rounded-lg overflow-hidden">
                <div className="p-4 hover:bg-gray-50 transition-colors">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center space-x-4">
                      <div className={`p-2 rounded-lg ${getActionColor(log.action)}`}>
                        {getActionIcon(log.action)}
                      </div>
                      <div>
                        <h4 className="font-medium text-gray-900">
                          {getActionDescription(log.action)}
                        </h4>
                        <div className="flex items-center space-x-4 text-sm text-gray-600 mt-1">
                          <span className="flex items-center space-x-1">
                            <User className="h-3 w-3" />
                            <span>{log.user_name || 'Não informado'}</span>
                          </span>
                          <span className="flex items-center space-x-1">
                            <FileText className="h-3 w-3" />
                            <span>{getUserRoleDescription(log.user_role)}</span>
                          </span>
                          <span className="flex items-center space-x-1">
                            <Clock className="h-3 w-3" />
                            <span>{formatDate(log.created_at)}</span>
                          </span>
                        </div>
                      </div>
                    </div>
                    <button
                      onClick={() => setExpandedLog(expandedLog === log.id ? null : log.id)}
                      className="text-gray-400 hover:text-gray-600"
                    >
                      {expandedLog === log.id ? 
                        <ChevronUp className="h-5 w-5" /> : 
                        <ChevronDown className="h-5 w-5" />
                      }
                    </button>
                  </div>

                  {/* Expanded Details */}
                  {expandedLog === log.id && (
                    <div className="mt-4 pt-4 border-t border-gray-200">
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-sm">
                        <div>
                          <span className="font-medium text-gray-700">Tipo de Recurso:</span>
                          <span className="ml-2 text-gray-900">
                            {getResourceTypeDescription(log.resource_type)}
                          </span>
                        </div>
                        <div>
                          <span className="font-medium text-gray-700">ID do Recurso:</span>
                          <span className="ml-2 text-gray-900 font-mono text-xs">
                            {log.resource_id}
                          </span>
                        </div>
                        <div>
                          <span className="font-medium text-gray-700">Endereço IP:</span>
                          <span className="ml-2 text-gray-900 font-mono">
                            {log.ip_address || 'Não registrado'}
                          </span>
                        </div>
                        <div>
                          <span className="font-medium text-gray-700">Navegador:</span>
                          <span className="ml-2 text-gray-900 text-xs">
                            {log.user_agent ? log.user_agent.substring(0, 50) + '...' : 'Não registrado'}
                          </span>
                        </div>
                      </div>
                      {log.details && Object.keys(log.details).length > 0 && (
                        <div className="mt-3">
                          <span className="font-medium text-gray-700">Detalhes Adicionais:</span>
                          <pre className="mt-1 text-xs text-gray-600 bg-gray-100 p-2 rounded overflow-x-auto">
                            {JSON.stringify(log.details, null, 2)}
                          </pre>
                        </div>
                      )}
                    </div>
                  )}
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  )
}